<template>
  <div class="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
    <CompanyAppLayout>
      <!-- Page Header -->
      <div class="pt-8 pb-6">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-3xl font-bold text-white mb-2">Company Profile</h1>
            <p class="text-slate-400">Manage your company profile, information, and settings</p>
          </div>
          <div class="flex items-center space-x-3">
            <button @click="saveProfile" class="btn-primary">
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
              </svg>
              Save Changes
            </button>
            <button @click="exportProfileData" class="btn-secondary">
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
              Export Data
            </button>
          </div>
        </div>
      </div>

      <!-- Profile Overview -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <!-- Company Information -->
        <div class="lg:col-span-2">
          <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6">
            <h2 class="text-xl font-semibold text-white mb-6">Company Information</h2>
            
            <form @submit.prevent="saveProfile" class="space-y-6">
              <!-- Avatar Section -->
              <div class="flex items-center space-x-6">
                <div class="relative">
                  <div class="w-24 h-24 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-full flex items-center justify-center overflow-hidden">
                    <img 
                      v-if="companyProfile.logoUrl" 
                      :src="companyProfile.logoUrl" 
                      alt="Company Logo" 
                      class="w-full h-full object-cover"
                    />
                    <span v-else class="text-2xl font-bold text-white">{{ companyProfile.name.charAt(0) }}</span>
                  </div>
                  <button 
                    @click="triggerLogoUpload" 
                    class="absolute -bottom-2 -right-2 w-8 h-8 bg-emerald-500 hover:bg-emerald-600 rounded-full flex items-center justify-center transition-colors"
                  >
                    <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"/>
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"/>
                    </svg>
                  </button>
                  <input 
                    ref="logoInput" 
                    type="file" 
                    accept="image/*" 
                    @change="handleLogoUpload" 
                    class="hidden"
                  />
                </div>
                <div>
                  <h3 class="text-lg font-medium text-white">{{ companyProfile.name }}</h3>
                  <p class="text-slate-400">{{ companyProfile.industry }} • {{ companyProfile.location }}</p>
                  <p class="text-sm text-slate-500">Member since {{ formatDate(companyProfile.createdAt) }}</p>
                </div>
              </div>

              <!-- Basic Information -->
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label class="block text-sm font-medium text-slate-300 mb-2">Company Name</label>
                  <input v-model="companyProfile.name" type="text" class="form-input w-full" required />
                </div>
                
                <div>
                  <label class="block text-sm font-medium text-slate-300 mb-2">Industry</label>
                  <select v-model="companyProfile.industry" class="form-select w-full" required>
                    <option value="">Select Industry</option>
                    <option value="oil-gas">Oil & Gas</option>
                    <option value="construction">Construction</option>
                    <option value="manufacturing">Manufacturing</option>
                    <option value="transportation">Transportation</option>
                    <option value="utilities">Utilities</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label class="block text-sm font-medium text-slate-300 mb-2">Location</label>
                  <input v-model="companyProfile.location" type="text" class="form-input w-full" required />
                </div>
                
                <div>
                  <label class="block text-sm font-medium text-slate-300 mb-2">Company Size</label>
                  <select v-model="companyProfile.size" class="form-select w-full" required>
                    <option value="">Select Size</option>
                    <option value="1-10">1-10 employees</option>
                    <option value="11-50">11-50 employees</option>
                    <option value="51-200">51-200 employees</option>
                    <option value="201-500">201-500 employees</option>
                    <option value="500+">500+ employees</option>
                  </select>
                </div>
              </div>

              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Description</label>
                <textarea v-model="companyProfile.description" class="form-input w-full" rows="4"></textarea>
              </div>

              <!-- Contact Information -->
              <div class="border-t border-slate-700/50 pt-6">
                <h3 class="text-lg font-medium text-white mb-4">Contact Information</h3>
                
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label class="block text-sm font-medium text-slate-300 mb-2">Email</label>
                    <input v-model="companyProfile.email" type="email" class="form-input w-full" required />
                  </div>
                  
                  <div>
                    <label class="block text-sm font-medium text-slate-300 mb-2">Phone</label>
                    <input v-model="companyProfile.phone" type="tel" class="form-input w-full" />
                  </div>
                </div>

                <div>
                  <label class="block text-sm font-medium text-slate-300 mb-2">Website</label>
                  <input v-model="companyProfile.website" type="url" class="form-input w-full" />
                </div>

                <div>
                  <label class="block text-sm font-medium text-slate-300 mb-2">Address</label>
                  <textarea v-model="companyProfile.address" class="form-input w-full" rows="3"></textarea>
                </div>
              </div>

              <!-- Business Information -->
              <div class="border-t border-slate-700/50 pt-6">
                <h3 class="text-lg font-medium text-white mb-4">Business Information</h3>
                
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label class="block text-sm font-medium text-slate-300 mb-2">Tax ID</label>
                    <input v-model="companyProfile.taxId" type="text" class="form-input w-full" />
                  </div>
                  
                  <div>
                    <label class="block text-sm font-medium text-slate-300 mb-2">Business License</label>
                    <input v-model="companyProfile.businessLicense" type="text" class="form-input w-full" />
                  </div>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label class="block text-sm font-medium text-slate-300 mb-2">Founded Year</label>
                    <input v-model="companyProfile.foundedYear" type="number" class="form-input w-full" />
                  </div>
                  
                  <div>
                    <label class="block text-sm font-medium text-slate-300 mb-2">Annual Revenue</label>
                    <select v-model="companyProfile.annualRevenue" class="form-select w-full">
                      <option value="">Select Revenue Range</option>
                      <option value="under-1m">Under $1M</option>
                      <option value="1m-10m">$1M - $10M</option>
                      <option value="10m-50m">$10M - $50M</option>
                      <option value="50m-100m">$50M - $100M</option>
                      <option value="over-100m">Over $100M</option>
                    </select>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>

        <!-- Stats Cards -->
        <div class="lg:col-span-1">
          <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6">
            <h3 class="text-lg font-semibold text-white mb-4">Company Stats</h3>
            
            <div class="space-y-4">
              <div class="flex items-center justify-between">
                <span class="text-sm text-slate-300">Total Employees</span>
                <span class="text-sm font-medium text-white">{{ companyStats.totalEmployees }}</span>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-sm text-slate-300">Active Jobs</span>
                <span class="text-sm font-medium text-emerald-400">{{ companyStats.activeJobs }}</span>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-sm text-slate-300">Completed Projects</span>
                <span class="text-sm font-medium text-cyan-400">{{ companyStats.completedProjects }}</span>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-sm text-slate-300">Success Rate</span>
                <span class="text-sm font-medium text-amber-400">{{ companyStats.successRate }}%</span>
              </div>
              <div class="border-t border-slate-700/50 pt-4">
                <div class="flex items-center justify-between">
                  <span class="text-lg font-semibold text-white">Rating</span>
                  <span class="text-lg font-bold text-emerald-400">{{ companyStats.rating }}/5</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Verification Status -->
          <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 mt-6">
            <h3 class="text-lg font-semibold text-white mb-4">Verification Status</h3>
            
            <div class="space-y-3">
              <div class="flex items-center justify-between">
                <span class="text-sm text-slate-300">Email Verified</span>
                <span class="text-sm text-emerald-400">✓ Verified</span>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-sm text-slate-300">Phone Verified</span>
                <span class="text-sm text-emerald-400">✓ Verified</span>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-sm text-slate-300">Business License</span>
                <span class="text-sm text-amber-400">Pending</span>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-sm text-slate-300">Insurance</span>
                <span class="text-sm text-emerald-400">✓ Verified</span>
              </div>
            </div>
          </div>

          <!-- Quick Actions -->
          <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 mt-6">
            <h3 class="text-lg font-semibold text-white mb-4">Quick Actions</h3>
            
            <div class="space-y-3">
              <button @click="updateVerification" class="quick-action-card">
                <svg class="w-6 h-6 text-emerald-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                <span class="text-sm font-medium text-white">Update Verification</span>
              </button>
              
              <button @click="downloadCertificates" class="quick-action-card">
                <svg class="w-6 h-6 text-cyan-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                </svg>
                <span class="text-sm font-medium text-white">Download Certificates</span>
              </button>
              
              <button @click="viewAnalytics" class="quick-action-card">
                <svg class="w-6 h-6 text-amber-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2zm0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
                </svg>
                <span class="text-sm font-medium text-white">View Analytics</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Certifications & Licenses -->
      <div class="mt-8">
        <h2 class="text-xl font-semibold text-white mb-6">Certifications & Licenses</h2>
        <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl overflow-hidden">
          <div class="overflow-x-auto">
            <table class="w-full">
              <thead class="bg-slate-700/50">
                <tr>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Certificate</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Type</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Issue Date</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Expiry Date</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Status</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-700/50">
                <tr v-for="certificate in certificates" :key="certificate.id" class="hover:bg-slate-700/30">
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-white">{{ certificate.name }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-300">{{ certificate.type }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-300">{{ formatDate(certificate.issueDate) }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-300">{{ formatDate(certificate.expiryDate) }}</td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span :class="[
                      'px-2 py-1 rounded-full text-xs font-medium',
                      certificate.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' :
                      certificate.status === 'expired' ? 'bg-red-500/20 text-red-400' :
                      'bg-amber-500/20 text-amber-400'
                    ]">
                      {{ certificate.status }}
                    </span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div class="flex items-center space-x-2">
                      <button @click="viewCertificate(certificate)" class="text-emerald-400 hover:text-emerald-300">
                        View
                      </button>
                      <button @click="downloadCertificate(certificate.id)" class="text-cyan-400 hover:text-cyan-300">
                        Download
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </CompanyAppLayout>
  </div>
</template>

<script setup lang="ts">
import CompanyAppLayout from '@/components/layout/CompanyAppLayout.vue'
import { useUserStore } from '@/stores/userStore'
import { onMounted, ref } from 'vue'

const userStore = useUserStore()

// Reactive data
const logoInput = ref(null)

const companyProfile = ref({
  name: '',
  industry: '',
  location: '',
  size: '',
  description: '',
  email: '',
  phone: '',
  website: '',
  address: '',
  taxId: '',
  businessLicense: '',
  foundedYear: '',
  annualRevenue: '',
  logoUrl: '',
  createdAt: ''
})

const companyStats = ref({
  totalEmployees: 0,
  activeJobs: 0,
  completedProjects: 0,
  successRate: 0,
  rating: 0
})

const certificates = ref([])

// Methods
const fetchCompanyProfile = async () => {
  try {
    const response = await fetch('/api/company/profile', {
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      companyProfile.value = data.profile
    }
  } catch (error) {
    console.error('Error fetching company profile:', error)
  }
}

const fetchCompanyStats = async () => {
  try {
    const response = await fetch('/api/company/stats', {
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      companyStats.value = data.stats
    }
  } catch (error) {
    console.error('Error fetching company stats:', error)
  }
}

const fetchCertificates = async () => {
  try {
    const response = await fetch('/api/company/certificates', {
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      certificates.value = data.certificates || []
    }
  } catch (error) {
    console.error('Error fetching certificates:', error)
  }
}

const saveProfile = async () => {
  try {
    const response = await fetch('/api/company/update-profile', {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(companyProfile.value)
    })
    
    if (response.ok) {
      // Show success message
      console.log('Profile updated successfully')
    }
  } catch (error) {
    console.error('Error updating profile:', error)
  }
}

const triggerLogoUpload = () => {
  logoInput.value.click()
}

const handleLogoUpload = async (event: any) => {
  const file = event.target.files[0]
  if (!file) return

  const formData = new FormData()
  formData.append('logo', file)

  try {
    const response = await fetch('/api/company/upload-logo', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`
      },
      body: formData
    })
    
    if (response.ok) {
      const data = await response.json()
      companyProfile.value.logoUrl = data.logoUrl
    }
  } catch (error) {
    console.error('Error uploading logo:', error)
  }
}

const updateVerification = () => {
  // Navigate to verification page
}

const downloadCertificates = () => {
  // Download all certificates
}

const viewAnalytics = () => {
  // Navigate to analytics page
}

const viewCertificate = (certificate: any) => {
  // View certificate details
}

const downloadCertificate = (certificateId: number) => {
  // Download specific certificate
}

const exportProfileData = () => {
  const csvContent = [
    ['Field', 'Value'],
    ['Company Name', companyProfile.value.name],
    ['Industry', companyProfile.value.industry],
    ['Location', companyProfile.value.location],
    ['Size', companyProfile.value.size],
    ['Email', companyProfile.value.email],
    ['Phone', companyProfile.value.phone],
    ['Website', companyProfile.value.website],
    ['Address', companyProfile.value.address],
    ['Tax ID', companyProfile.value.taxId],
    ['Business License', companyProfile.value.businessLicense],
    ['Founded Year', companyProfile.value.foundedYear],
    ['Annual Revenue', companyProfile.value.annualRevenue]
  ].map(row => row.join(',')).join('\n')

  const blob = new Blob([csvContent], { type: 'text/csv' })
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'company-profile.csv'
  a.click()
  window.URL.revokeObjectURL(url)
}

const formatDate = (date: string) => {
  return new Date(date).toLocaleDateString()
}

// Lifecycle
onMounted(async () => {
  await Promise.all([
    fetchCompanyProfile(),
    fetchCompanyStats(),
    fetchCertificates()
  ])
})
</script>

<style scoped>
.quick-action-card {
  @apply flex items-center w-full p-3 bg-slate-700/30 rounded-lg hover:bg-slate-600/50 transition-colors;
}

.btn-primary {
  @apply px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded-lg transition-colors flex items-center;
}

.btn-secondary {
  @apply px-4 py-2 bg-slate-700/50 hover:bg-slate-600/50 text-slate-300 font-medium rounded-lg transition-colors flex items-center;
}

.form-input, .form-select {
  @apply bg-slate-700/50 border border-slate-600/50 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent;
}

.form-select {
  @apply min-w-[120px];
}
</style>
